import type { LocalFile, LocalOrSavedFile, SavedFile } from '@frontend/api-types';
import {
  EMAIL_REGEX,
  capitalize,
  convertEncodedToDecodedURI,
  dayjs,
  isStringValidDateISOString,
  isValidEncodedURI,
  makeDateISOString,
  makeEncodedURI,
  makeIanaTimezone,
  match,
} from '@frontend/duck-tape';
import { isValidUrlOrDomain } from '@frontend/duck-tape/url/url';
import * as LPN from 'libphonenumber-js';
import * as z from 'zod';

type FileType = 'localFile' | 'localOrSavedFile' | 'savedFile';

const fileArrayValidator = <TFileType extends FileType>(type: TFileType) => z.array(fileValidator<TFileType>(type)!);

const fileValidator = <TFileType extends FileType>(
  type: TFileType,
): TFileType extends 'localFile'
  ? z.ZodType<LocalFile, z.ZodTypeDef, LocalFile>
  : TFileType extends 'savedFile'
    ? z.ZodType<SavedFile, z.ZodTypeDef, SavedFile>
    : z.ZodType<LocalOrSavedFile, z.ZodTypeDef, LocalOrSavedFile> => {
  return (
    match(type)
      // @ts-expect-error Tedious types
      .with('localFile', () => z.custom<LocalFile>())
      // @ts-expect-error Tedious types
      .with('savedFile', () => z.custom<SavedFile>())
      // @ts-expect-error Tedious types
      .with('localOrSavedFile', () => z.custom<LocalOrSavedFile>())
      // @ts-expect-error Tedious types
      .exhaustive()
  );
};

const looseUrlValidator = ({ isOptional = false }: { isOptional?: boolean } = {}) => {
  const base = z.string().refine((value) => (isOptional && !value ? true : isValidUrlOrDomain(value)), {
    message: 'Please provide a valid url',
  });
  if (isOptional) return base.optional();
  return base;
};

const positiveInteger = () => z.number().int().positive();

const numericString = () =>
  z
    .string()
    .refine(
      (value) => {
        return !isNaN(parseFloat(value)) && isFinite(value as unknown as number);
      },
      {
        message: 'Invalid numeric string', // Custom error message
      },
    )
    .transform((v) => parseInt(v, 10));

const fiveStarReview = () => z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]);

const dateISOString = (errorMessage = 'Invalid ISO 8601 date format') =>
  z
    .string()
    .refine(isStringValidDateISOString, {
      message: errorMessage,
    })
    .transform(makeDateISOString);

const birthdateAdult = (errorMessage = 'Must be at least 18 years old') =>
  dateISOString('Please enter a valid birthdate')
    .refine(
      (birthdate) => {
        const eighteenYearsAgo = dayjs().subtract(18, 'year');
        return dayjs(birthdate).isBefore(eighteenYearsAgo) || dayjs(birthdate).isSame(eighteenYearsAgo);
      },
      {
        message: errorMessage,
      },
    )
    .refine(
      (birthdate) => {
        const tooOldThreshold = dayjs().subtract(120, 'year');
        return dayjs(birthdate).isAfter(tooOldThreshold);
      },
      {
        message: 'Must be less than 120 years old',
      },
    );

const encodedURI = () =>
  z
    .string()
    .refine(isValidEncodedURI, {
      message: 'Invalid encoded uri',
    })
    .transform(makeEncodedURI)
    .transform(convertEncodedToDecodedURI);

const ianaTimezone = () => z.string().transform(makeIanaTimezone);

const readonlyStringEnum = <T extends string>(values: T[]) => z.enum(values as [T, ...T[]]);

const emailPattern = EMAIL_REGEX.source.replace(/^\^|\$$/g, '');
export const emailWithOptImposterRegex = RegExp(`^${emailPattern}( +as +${emailPattern})?$`);

// Custom validator for numeric strings or numbers transformed into strings
const optionalStringOrNumber = () => z.union([z.string(), z.number()]).optional();

export const validators = {
  NANPPhoneNumber: (fieldName: string = 'Phone number') =>
    z.string({ message: `${capitalize(fieldName)} is required` }).refine(
      (value) => {
        const phoneNumberPattern = /^\+1 \d{3}-\d{3}-\d{4}$/;
        return phoneNumberPattern.test(value);
      },
      {
        message: 'Invalid phone number format. Expected format: +1 000-000-0000',
      },
    ),
  array: z.array,
  birthdateAdult,
  boolean: z.boolean,
  // By default z.number({coerce: true}) will coerce empty strings to 0, which is not what we want, but we do want to parse numerical strings
  // into a number if possible
  coercedNumber: () =>
    z.preprocess((val) => {
      if (typeof val === 'string') {
        const s = val.trim();
        if (s === '') return undefined;
        const n = Number(s);
        return Number.isFinite(n) ? n : val; // fall through to z.number() for errors
      }
      return val;
    }, z.number()),
  dateISOString,
  e164PhoneNumber: (fieldName: string = 'Phone number') =>
    z.string({ message: `${capitalize(fieldName)} is required` }).refine(
      (phoneNumber) => {
        if (phoneNumber === '') return true;

        // Try to parse the phone number
        const parsedNumber = LPN.parsePhoneNumberFromString(phoneNumber);

        // Validate if it's a valid number and in E.164 format
        return parsedNumber && LPN.isValidNumber(phoneNumber) && parsedNumber.format('E.164') === phoneNumber;
      },
      {
        message: 'Invalid US phone number format',
      },
    ),
  email: () => z.string({ message: 'Email cannot be blank' }).regex(EMAIL_REGEX, 'Invalid email').min(1),
  encodedURI,
  fileArrayValidator,
  fileValidator,
  fiveStarReview,
  ianaTimezone,
  integer: () =>
    z.number().refine((val) => Number.isInteger(val), {
      message: 'Number must be an integer with no decimal places',
    }),
  internationalPhoneNumber: (fieldName: string = 'Phone number') =>
    z.string({ message: `${capitalize(fieldName)} is required` }).refine(
      (phoneNumber) => {
        const parsedNumber = LPN.parsePhoneNumberFromString(phoneNumber);
        return parsedNumber && LPN.isValidNumber(phoneNumber);
      },
      {
        message: 'Invalid phone number format. Please provide a valid phone number.',
      },
    ),
  legacyPassword: () => z.string({ message: 'Password is required' }),
  literal: z.literal,
  loginEmail: () =>
    z.string({ message: 'Email cannot be blank' }).regex(emailWithOptImposterRegex, 'Invalid email').min(1),
  looseUrlValidator,
  never: z.never,

  number: z.number,
  numberOrString: () => z.union([z.number(), z.string()]),
  numericString,
  object: z.object,
  optionalString: () => z.string().optional().default(''),
  optionalStringOrNumber,
  password: () =>
    z
      .string({ message: 'Password is required' })
      .min(8, { message: 'Password must be at least 8 characters' })
      .refine((value) => /\d/.test(value), {
        message: 'Password must contain at least 1 number',
      }),
  /** Legacy password validator (12+ characters) for old signup flow when is_signup_changes_enabled=false */
  passwordLegacy: () =>
    z.string({ message: 'Password is required' }).min(12, { message: 'Password must be at least 12 characters' }),
  positiveInteger,
  readonlyStringEnum,
  requiredString: (fieldName?: string) =>
    z.string({ message: fieldName ? `${capitalize(fieldName)} cannot be blank` : 'This field is required' }).min(1),
  string: z.string,
  undefined: z.undefined,
  union: z.union,
  usZipCode: () =>
    z.string({ message: 'Zip code is required' }).refine(
      (value) => {
        const zipCodePattern = /^\d{5}(?:-\d{4})?$/;
        return zipCodePattern.test(value);
      },
      {
        message: 'Invalid zip code format. Expected format: 12345 or 12345-6789',
      },
    ),
  uuid: () => z.string().uuid(),
};
export const validateUSZipCode = (zipCode: string) => validators.usZipCode().safeParse(zipCode);

export const validateE164USPhoneNumber = (phoneNumber: string) => validators.e164PhoneNumber().safeParse(phoneNumber);

export const validateNANPPhoneNumber = (phoneNumber: string) => validators.NANPPhoneNumber().safeParse(phoneNumber);

export const uuidPattern = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
export const uuidRegex = new RegExp(uuidPattern);

export { z };
